'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Hash,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { useTranslation } from '@/components/i18n-provider';
import { useAuth } from '@/components/auth-provider';
import { Avatar } from '@/components/avatar';
import { cn } from '@/lib/utils';
import { usePanelBackStack } from '@/components/workspace/panels/use-panel-back';
import { ChannelPanelView } from '@/components/marketplace/channel-panel-view';

interface Subchannel {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  iconEmoji?: string | null;
  logoUrl?: string | null;
  memberCount?: number;
  postCount?: number;
}

interface ChannelSection {
  id: string;
  name: string;
  slug: string;
  iconEmoji?: string | null;
  description?: string | null;
  logoUrl?: string | null;
  memberCount?: number;
  postCount?: number;
  channels: Subchannel[];
}

const PAGE_SIZE = 20;

function SubchannelRow({ sub, onOpen }: { sub: Subchannel; onOpen: (slug: string, name: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(sub.slug, sub.name)}
      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent/50 transition-colors group"
    >
      <Avatar seed={sub.slug} type="channel" src={sub.logoUrl} className="h-8 w-8" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium truncate group-hover:text-foreground transition-colors">
            {sub.name}
          </span>
        </div>
        <span className="text-xs text-muted-foreground truncate block">//{sub.slug}</span>
      </div>
    </button>
  );
}

function ChannelSectionCard({ section, defaultOpen, onOpen, pinned }: { section: ChannelSection; defaultOpen: boolean; onOpen: (slug: string, name: string) => void; pinned?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const subCount = section.channels.length;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={cn(
        'rounded-xl border transition-all',
        open
          ? 'border-border bg-card shadow-sm'
          : 'border-transparent hover:border-border hover:bg-card/50'
      )}>
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center gap-3 p-3.5 text-left cursor-pointer group">
            <div className="relative shrink-0">
              {section.iconEmoji ? (
                <span className="flex items-center justify-center h-10 w-10 rounded-xl bg-accent text-lg">
                  {section.iconEmoji}
                </span>
              ) : (
                <Avatar seed={section.slug} type="channel" src={section.logoUrl} className="h-10 w-10 rounded-xl" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold truncate">{section.name}</h3>
                {pinned && (
                  <span className="shrink-0 rounded-md border border-border/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Pinned
                  </span>
                )}
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {subCount} {subCount === 1 ? t('home.subchannel') : t('home.subchannels')}
                </span>
              </div>
              {/* Lead with the slug: searching "design" returns several
                  channels whose display name is just "Design", and without it
                  the rows are indistinguishable. Matches how feed cards and
                  the composer identify a channel. */}
              <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                <span className="font-medium">//{section.slug}</span>
                {section.description ? ` · ${section.description}` : ''}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onOpen(section.slug, section.name); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onOpen(section.slug, section.name); } }}
                className="text-xs font-medium text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-lg hover:bg-accent transition-colors"
              >
                View
              </span>
              <ChevronRight className={cn(
                'h-4 w-4 text-muted-foreground transition-transform duration-200',
                open && 'rotate-90'
              )} />
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-border mx-3" />
          <div className="p-2 space-y-0.5">
            {section.channels.map(sub => (
              <SubchannelRow key={sub.id} sub={sub} onOpen={onOpen} />
            ))}
            {section.channels.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-3">No subchannels yet</p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

interface HomeChannelsTabProps {
  feed: string;
  /** Debounced channel-name query from the feed's search box. When set, the
   *  categorized listing is replaced by a flat search result. */
  search?: string;
}

/** The marketplace roots, curated + pinned to the top, rendered as full section
 *  cards (subchannels = the category channels), same as any other section. */
const PINNED_ROOTS = [
  { root: 'apps', name: 'Apps', description: 'Browse every app in the marketplace' },
  { root: 'agents', name: 'Agents', description: 'Browse every agent in the marketplace' },
];

export function HomeChannelsTab({ feed, search = '' }: HomeChannelsTabProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [sections, setSections] = useState<ChannelSection[]>([]);
  const [pinned, setPinned] = useState<ChannelSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);
  // Open a channel as an IN-PANEL drill-down (a native ChannelPanelView with a
  // Back button), matching the Apps / Agents Explore views — never a fullscreen
  // navigation, never an iframe. A STACK, so drilling `//apps/collaboration` →
  // `.../collab-board` and pressing Back returns one level (to collaboration),
  // not all the way to the root list.
  const [drill, setDrill] = useState<{ slug: string; name: string }[]>([]);
  const openChannel = useCallback(
    (slug: string, name: string) => setDrill((s) => [...s, { slug, name }]),
    [],
  );
  const popChannel = useCallback(() => setDrill((s) => s.slice(0, -1)), []);
  const detail = drill[drill.length - 1] ?? null;
  usePanelBackStack(drill.length, popChannel); // browser Back pops ONE drill level

  const isFollowingMode = feed === 'following';


  const fetchChannels = useCallback(async (reset: boolean) => {
    if (!reset && loadingMoreRef.current) return;
    if (reset) {
      setLoading(true);
      setSections([]);
      setOffset(0);
    } else {
      setLoadingMore(true);
      loadingMoreRef.current = true;
    }

    const currentOffset = reset ? 0 : offset;
    const q = search.trim();
    try {
      let url: string;
      if (q) {
        // Search uses the flat `/api/channels?search=` branch (the
        // categorized branch ignores the query) and returns `{ channels }`.
        const sort = feed === 'new' ? 'newest' : 'trending';
        url = `/api/channels?search=${encodeURIComponent(q)}&sort=${sort}&limit=${PAGE_SIZE}&offset=${currentOffset}`;
      } else if (isFollowingMode && user) {
        url = `/api/users/me/channels`;
      } else {
        const sort = feed === 'new' ? 'newest' : 'trending';
        url = `/api/channels?include_categories=true&limit=${PAGE_SIZE}&offset=${currentOffset}&sort=${sort}`;
      }

      const res = await fetch(url);

      if (!res.ok) { setHasMore(false); return; }
      const data = await res.json();

      if (q) {
        // Flat search results — dedupe-append for infinite scroll.
        const flatChannels: ChannelSection[] = (data.channels || []).map((ch: any) => ({
          ...ch,
          channels: [],
        }));
        if (reset) {
          setSections(flatChannels);
          setOffset(flatChannels.length);
        } else {
          setSections((prev) => {
            const ids = new Set(prev.map((s) => s.id));
            return [...prev, ...flatChannels.filter((s) => !ids.has(s.id))];
          });
          setOffset((prev) => prev + flatChannels.length);
        }
        setHasMore(data.hasMore === true || flatChannels.length === PAGE_SIZE);
      } else if (isFollowingMode) {
        const flatChannels: ChannelSection[] = (data.channels || []).map((ch: any) => ({
          ...ch,
          channels: [],
        }));
        setSections(flatChannels);
        setHasMore(false);
      } else {
        const newSections: ChannelSection[] = data.categories || [];

        if (reset) {
          setSections(newSections);
          setOffset(newSections.length);
        } else {
          setSections(prev => {
            const ids = new Set(prev.map(s => s.id));
            const unique = newSections.filter(s => !ids.has(s.id));
            return [...prev, ...unique];
          });
          setOffset(prev => prev + newSections.length);
        }
        setHasMore(data.hasMore === true);
      }
    } catch {
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [feed, offset, isFollowingMode, user, search]);

  useEffect(() => {
    fetchChannels(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed, search]);

  // Pinned marketplace roots (//apps, //agents) as full section cards — their
  // subchannels are the category channels (Games, Creative, …).
  useEffect(() => {
    const ctrl = new AbortController();
    Promise.all(
      PINNED_ROOTS.map((r) =>
        fetch(`/api/marketplace/channels?root=${r.root}`, { signal: ctrl.signal })
          .then((res) => (res.ok ? res.json() : null))
          .then((d): ChannelSection | null =>
            d
              ? {
                  id: r.root,
                  name: r.name,
                  slug: r.root,
                  description: r.description,
                  channels: ((d.categories as Array<{ slug: string; name: string; iconEmoji?: string | null }>) || []).map((c) => ({
                    id: c.slug,
                    name: c.name,
                    slug: c.slug,
                    iconEmoji: c.iconEmoji,
                  })),
                }
              : null,
          )
          .catch(() => null),
      ),
    ).then((rs) => {
      if (!ctrl.signal.aborted) setPinned(rs.filter((s): s is ChannelSection => !!s));
    });
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading) return;
    const scrollRoot = document.getElementById('main-scroll');
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loadingMoreRef.current) {
          fetchChannels(false);
        }
      },
      { rootMargin: '300px', root: scrollRoot },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, loading, fetchChannels]);

  if (detail) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="mb-2 flex shrink-0 items-center gap-1.5 border-b border-border/60 pb-2">
          <button
            type="button"
            onClick={popChannel}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <span className="truncate text-sm font-medium">{detail.name}</span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ChannelPanelView
            slug={detail.slug}
            name={detail.name}
            canPost={!!user}
            onOpen={openChannel}
            onDeleted={() => {
              popChannel();
              void fetchChannels(true);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* //apps and //agents pinned to the top, as full section cards.
          Hidden while searching — the results below are the answer. */}
      {!isFollowingMode && !search.trim() && pinned.length > 0 && (
        <div className="mb-2 space-y-2">
          {pinned.map((section) => (
            <ChannelSectionCard key={section.id} section={section} defaultOpen onOpen={openChannel} pinned />
          ))}
        </div>
      )}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-border p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-muted shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-1/3" />
                  <div className="h-3 bg-muted rounded w-2/3" />
                </div>
                <div className="h-4 w-4 bg-muted rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : sections.length === 0 ? (
        <div className="text-center py-16">
          <Hash className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold text-lg mb-2">
            {isFollowingMode ? t('feed.emptyFollowing') : t('home.emptyChannels')}
          </h3>
          <p className="text-muted-foreground">
            {isFollowingMode ? t('feed.emptyFollowingDesc') : t('home.emptyChannelsDesc')}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {sections.map((section) => (
              <ChannelSectionCard
                key={section.id}
                section={section}
                defaultOpen={false}
                onOpen={openChannel}
              />
            ))}
          </div>

          {hasMore && (
            <div ref={sentinelRef} className="flex justify-center py-6">
              {loadingMore && <Spinner size="lg" tone="muted" />}
            </div>
          )}

          {!hasMore && sections.length > 0 && (
            <p className="text-center text-sm text-muted-foreground py-4">
              {t('home.reachedEnd')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
